import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, Wallet, ArrowLeft, Download } from "lucide-react";

const ROBBIE = "/img/mascots/robbie-winkt.webp";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/* Sicherheit: Nur vertrauenswürdige Ziel-URLs zulassen (kein javascript:, kein Open-Redirect).
   Apple  = eigener .pkpass-Endpunkt auf dem Backend.
   Google = entweder die offizielle Save-URL (pay.google.com) ODER ein eigener Backend-Redirect-
            Endpunkt (/api/wallet/.../google[/save]), der serverseitig per 302 auf pay.google.com
            zeigt. So steht das lange JWT nicht in der URL und die Allowlist bleibt strikt
            (ausschließlich pay.google.com + eigenes Backend). */
const safeApple = (u) => (u && isOwnWallet(u) ? u : "");
const safeGoogle = (u) => {
  if (!u) return "";
  if (u.startsWith("https://pay.google.com/gp/v/save/")) return u;
  if (isOwnWallet(u)) return u;
  return "";
};

/* true, wenn u auf unseren eigenen Wallet-API-Pfad zeigt – akzeptiert sowohl die zur Build-Zeit
   gesetzte Backend-URL als auch die aktuell geöffnete Origin (schützt vor www/apex-Abweichungen
   zwischen REACT_APP_BACKEND_URL und der Live-Domain). Kein Open-Redirect: nur eigener Host. */
function isOwnWallet(u) {
  if (BACKEND && u.startsWith(`${BACKEND}/api/wallet/`)) return true;
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (origin && u.startsWith(`${origin}/api/wallet/`)) return true;
  } catch { /* ignore */ }
  return u.startsWith("/api/wallet/");
}

/**
 * Markengerechte Zwischenseite für „Zu Wallet hinzufügen".
 * Apple: startet den .pkpass-Download automatisch per verstecktem iframe (Seite bleibt sichtbar,
 *        keine weiße Browserseite) und zeigt Hinweis + Fallback-Button + Zurück.
 * Google: leitet markengerecht weiter (location.replace → Zurück führt sauber zur Ausgangsseite).
 * Die wichtigen Elemente (Hinweistext, Fallback-Button, Zurück) sind immer sichtbar – unabhängig
 * davon, ob die automatische Bestätigung schon durchlief.
 */
export default function WalletAddPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const provider = params.get("p") === "google" ? "google" : "apple";
  const kind = params.get("kind") === "ticket" ? "ticket" : "card";
  const appleUrl = safeApple(params.get("a") || "");
  const googleUrl = safeGoogle(params.get("g") || "");
  const invalid = provider === "apple" ? !appleUrl : !googleUrl;
  const noun = kind === "ticket" ? "dein Ticket" : "deine Kundenkarte";
  const nounCap = kind === "ticket" ? "Dein Ticket" : "Deine Kundenkarte";
  const pron = kind === "ticket" ? "es" : "sie";

  const iframeRef = useRef(null);
  const [done, setDone] = useState(false);

  const triggerApple = useCallback(() => {
    if (iframeRef.current && appleUrl) iframeRef.current.src = appleUrl;
  }, [appleUrl]);

  const goGoogle = useCallback(() => { if (googleUrl) window.location.replace(googleUrl); }, [googleUrl]);

  useEffect(() => {
    document.title = "Zu Wallet hinzufügen · DIMEDIAL";
    if (invalid) return;
    if (provider === "apple") {
      triggerApple();
      const t = setTimeout(() => setDone(true), 1500);
      return () => clearTimeout(t);
    }
    const t = setTimeout(goGoogle, 900);
    return () => clearTimeout(t);
  }, [provider, invalid, triggerApple, goGoogle]);

  const back = () => { if (window.history.length > 1) navigate(-1); else navigate("/"); };

  if (invalid) {
    return (
      <div className="min-h-screen bg-[#111] text-white flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="wallet-add-page">
        <div className="w-full max-w-sm flex flex-col items-center">
          <img src={ROBBIE} alt="Robbie" className="w-28 h-28 object-contain mb-6 drop-shadow-[0_8px_24px_rgba(253,209,5,0.3)]" />
          <h1 className="font-display text-2xl mb-3">Link ungültig</h1>
          <p className="text-sm text-white/80 leading-relaxed" data-testid="wallet-add-message">
            Dieser Wallet-Link ist ungültig oder abgelaufen. Bitte öffne {noun} erneut über deine DIMEDIAL-Seite.
          </p>
          <button type="button" onClick={back} className="mt-8 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition" data-testid="wallet-add-back">
            <ArrowLeft className="w-4 h-4" /> Zurück
          </button>
          <p className="mt-10 text-[11px] text-white/35">Powered by DIMEDIAL.studio</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111] text-white flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="wallet-add-page">
      <div className="w-full max-w-sm flex flex-col items-center">
        <img src={ROBBIE} alt="Robbie" className="w-28 h-28 object-contain mb-6 drop-shadow-[0_8px_24px_rgba(253,209,5,0.3)]" />

        {provider === "apple" ? (
          <>
            <div className="flex items-center justify-center gap-2 mb-3" data-testid="wallet-add-status">
              {done
                ? <span className="inline-flex items-center gap-2 text-[#FDD105] font-bold"><CheckCircle2 className="w-5 h-5" /> Fertig!</span>
                : <span className="inline-flex items-center gap-2 text-white/80"><Loader2 className="w-5 h-5 animate-spin text-[#FDD105]" /> Wird hinzugefügt…</span>}
            </div>
            <h1 className="font-display text-2xl mb-3">{nounCap} für Apple Wallet</h1>
            <p className="text-sm text-white/80 leading-relaxed" data-testid="wallet-add-message">
              {done
                ? <>Fertig – {noun} ist jetzt in <b>Apple Wallet</b>. Öffne die Wallet-App, um {pron} anzusehen.</>
                : <>Der Download startet automatisch. Tippe anschließend oben auf <b>„Hinzufügen"</b> – danach liegt {noun} in <b>Apple Wallet</b>.</>}
            </p>
            <button type="button" onClick={triggerApple} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FDD105] text-[#111] px-5 py-3 text-sm font-bold hover:brightness-95 transition" data-testid="wallet-add-retry">
              <Download className="w-4 h-4" /> Nicht gestartet? Erneut herunterladen
            </button>
            <a href={appleUrl} className="mt-3 text-xs text-white/55 hover:text-white underline" data-testid="wallet-add-direct">Alternativ direkt öffnen</a>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="inline-flex items-center gap-2 text-white/80"><Loader2 className="w-5 h-5 animate-spin text-[#FDD105]" /> Wird geöffnet…</span>
            </div>
            <h1 className="font-display text-2xl mb-3">{nounCap} für Google Wallet</h1>
            <p className="text-sm text-white/80 leading-relaxed" data-testid="wallet-add-message">
              Du wirst zu <b>Google Wallet</b> weitergeleitet, um {noun} zu speichern. Falls das nicht automatisch passiert, tippe hier:
            </p>
            <button type="button" onClick={goGoogle} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FDD105] text-[#111] px-5 py-3 text-sm font-bold hover:brightness-95 transition" data-testid="wallet-add-google-manual">
              <Wallet className="w-4 h-4" /> Weiter zu Google Wallet
            </button>
          </>
        )}

        <button type="button" onClick={back} className="mt-8 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition" data-testid="wallet-add-back">
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>

        <p className="mt-10 text-[11px] text-white/35">Powered by DIMEDIAL.studio</p>
      </div>

      {provider === "apple" && <iframe ref={iframeRef} title="pkpass" className="hidden" onLoad={() => setDone(true)} />}
    </div>
  );
}
